/**
 * Master List — catalog picker for Traxccel Framework bundles.
 * Same layout as the Capability Catalog (server-paged, grouped by source framework),
 * with checkboxes to select capabilities and sub-capabilities. All questions under
 * the selection are included automatically on create (the API expands them;
 * preview via the eye button).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { ChevronDown, Eye, Layers, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";

import Menu from "../components/menu";
import DashboardShell from "../../components/DashboardShell";
import SaListPageShell from "../../components/sa/SaListPageShell";
import CapabilityCatalogTable, {
  type CapabilityCatalogSelection,
} from "../../components/sa/CapabilityCatalogTable";
import CatalogFilterChip from "../../components/sa/CatalogFilterChip";
import { Alert, Button as UiButton } from "../../components/ui";
import { useMenuState } from "../../Common/MenuStateContext";
import ContentLoader from "../../components/ContentLoader";
import {
  getCapabilityCatalog,
  getCapabilityCatalogFilterOptions,
  getCapabilityDetail,
} from "../../services/capabilitiesApi";
import { getFrameworks, getSourceTypes } from "../../services/frameworkApi";
import { buildSourceTypeById, type SourceTypeRef } from "../../utils/sourceTypeCatalog";
import { addOrUpdateTraxccelFramework } from "../../services/traxccelFrameworkApi";
import { dashboardToast } from "../../utils/dashboardToast";
import {
  EMPTY_MASTER_LIST_ROWS,
  MASTER_LIST_FILTER_ANY,
  MASTER_LIST_PAGE_COPY,
  buildMasterListRows,
  buildQuestionPreview,
  collectCatalogIdsFromMasterListRows,
  emptyMasterListFilters,
  formatMissingSubSelectionMessage,
  getCapabilitiesMissingSubSelection,
  getCapabilityPickerStatus,
  getMasterListRowLabel,
  groupRowsByCapabilityId,
  type MasterListColumnKey,
  type MasterListQuestionPreview,
  type MasterListRow,
} from "../../utils/masterListUtils";
import {
  buildCapabilityCatalogQuery,
  CAPABILITY_CATALOG_PAGE_SIZE,
  getCapabilityCatalogFilterableColumns,
  mapCapabilityCatalogItemsToMasterList,
  sanitizeCatalogColumnFilters,
  type CapabilityCatalogFilterOptions,
} from "../../utils/capabilityCatalogUtils";
import { FRAMEWORK_STUDIO_LABELS } from "../../utils/frameworkStudioLabels";
import "../../assets/css/dashboard.css";

const CAPABILITY_VIEW = "capability" as const;
const emptyRowMap = () => new Map<string, MasterListRow>();

const cx = (...parts: Array<string | false | null | undefined>) =>
  parts.filter(Boolean).join(" ");

const QuestionsPreviewModal = ({
  preview,
  onClose,
}: {
  preview: MasterListQuestionPreview | null;
  onClose: () => void;
}) => (
  <Modal show={Boolean(preview)} onHide={onClose} centered size="lg">
    <Modal.Header closeButton>
      <Modal.Title>{preview?.title || "Questions"}</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      {preview?.subtitle ? (
        <p className="master-list-preview-subtitle text-muted mb-3">{preview.subtitle}</p>
      ) : null}
      <p className="master-list-preview-note mb-3">
        These questions are included automatically when you create an assessment framework.
      </p>
      <div className="master-list-qgroups">
        <section className="master-list-qgroup">
          <header className="master-list-qgroup__head">
            <span className="master-list-qgroup__label">Current State</span>
            <span className="master-list-qgroup__count">
              {preview?.current.length || 0}{" "}
              {(preview?.current.length || 0) === 1 ? "question" : "questions"}
            </span>
          </header>
          {preview?.current.length ? (
            <ul className="master-list-preview-list">
              {preview.current.map((row) => (
                <li key={row.rowKey} className="master-list-preview-item">
                  {row.questionText}
                </li>
              ))}
            </ul>
          ) : (
            <p className="master-list-nest__empty mb-0">No current state questions.</p>
          )}
        </section>
        <section className="master-list-qgroup">
          <header className="master-list-qgroup__head">
            <span className="master-list-qgroup__label">Future State</span>
            <span className="master-list-qgroup__count">
              {preview?.future.length || 0}{" "}
              {(preview?.future.length || 0) === 1 ? "question" : "questions"}
            </span>
          </header>
          {preview?.future.length ? (
            <ul className="master-list-preview-list">
              {preview.future.map((row) => (
                <li key={row.rowKey} className="master-list-preview-item">
                  {row.questionText}
                </li>
              ))}
            </ul>
          ) : (
            <p className="master-list-nest__empty mb-0">No future state questions.</p>
          )}
        </section>
      </div>
    </Modal.Body>
    <Modal.Footer>
      <Button variant="outline-secondary" onClick={onClose}>
        Close
      </Button>
    </Modal.Footer>
  </Modal>
);

const MasterList = () => {
  const { isMenuCollapsed, toggleMenuCollapse } = useMenuState();
  const navigate = useNavigate();
  const [selectedItem, setSelectedItem] = useState(null);

  // Catalog page (server-paged, same endpoint as the Capability Catalog)
  const [catalogRows, setCatalogRows] = useState<MasterListRow[]>([]);
  const [subsByCapabilityId, setSubsByCapabilityId] = useState(
    () => new Map<number, MasterListRow[]>()
  );
  const [catalogFilterOptions, setCatalogFilterOptions] = useState<CapabilityCatalogFilterOptions>(
    {}
  );
  const [catalogPage, setCatalogPage] = useState(1);
  const [catalogTotalCount, setCatalogTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [frameworkFilter, setFrameworkFilter] = useState("");
  const [sourceTypeFilter, setSourceTypeFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState(() => emptyMasterListFilters(CAPABILITY_VIEW));
  const [frameworks, setFrameworks] = useState([]);
  const [sourceTypes, setSourceTypes] = useState<SourceTypeRef[]>([]);

  // Selection — kept by row key so it survives paging, search and filters
  const [selectedCaps, setSelectedCaps] = useState(emptyRowMap);
  const [selectedSubs, setSelectedSubs] = useState(emptyRowMap);
  const [revealCapabilityKey, setRevealCapabilityKey] = useState<string | null>(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createTitle, setCreateTitle] = useState("");
  const [createDescription, setCreateDescription] = useState("");
  const [creating, setCreating] = useState(false);
  const [showIssueDetails, setShowIssueDetails] = useState(false);
  const [questionPreview, setQuestionPreview] = useState<MasterListQuestionPreview | null>(null);
  const [questionPreviewLoading, setQuestionPreviewLoading] = useState(false);

  const handleMenuSelection = useCallback((item) => {
    setSelectedItem(item);
    return item;
  }, []);

  const catalogFilterableKeys = useMemo(
    () => getCapabilityCatalogFilterableColumns().map((column) => column.key),
    []
  );

  const frameworkById = useMemo(
    () => Object.fromEntries((frameworks || []).map((fw) => [fw.id, fw])),
    [frameworks]
  );
  const sourceTypeById = useMemo(() => buildSourceTypeById(sourceTypes), [sourceTypes]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [frameworkResult, sourceTypeList] = await Promise.all([
          getFrameworks(1, 500),
          getSourceTypes("", true),
        ]);
        if (cancelled) return;
        setFrameworks(frameworkResult.items || []);
        setSourceTypes((sourceTypeList || []) as SourceTypeRef[]);
      } catch (err) {
        console.error("Failed to load catalog filters:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setCatalogPage(1);
  }, [debouncedSearch, frameworkFilter, sourceTypeFilter]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const queryInput = {
          search: debouncedSearch,
          frameworkId: frameworkFilter,
          sourceTypeId: sourceTypeFilter,
          columnFilters,
        };
        const [pageResult, filterOptions] = await Promise.all([
          getCapabilityCatalog(buildCapabilityCatalogQuery({ ...queryInput, pageNumber: catalogPage })),
          getCapabilityCatalogFilterOptions(
            buildCapabilityCatalogQuery({
              ...queryInput,
              pageNumber: 1,
              pageSize: CAPABILITY_CATALOG_PAGE_SIZE,
            })
          ),
        ]);
        if (cancelled) return;

        const sanitizedFilters = sanitizeCatalogColumnFilters(
          columnFilters,
          filterOptions,
          catalogFilterableKeys
        );
        if (sanitizedFilters !== columnFilters) {
          setColumnFilters(sanitizedFilters);
        }
        setCatalogFilterOptions(filterOptions);

        const { rows, subsByCapabilityId: subsMap } = mapCapabilityCatalogItemsToMasterList(
          pageResult.items
        );
        setCatalogRows(rows);
        setSubsByCapabilityId(subsMap);
        setCatalogTotalCount(pageResult.totalCount);

        const totalPages = Math.max(
          1,
          Math.ceil(pageResult.totalCount / CAPABILITY_CATALOG_PAGE_SIZE)
        );
        if (catalogPage > totalPages) {
          setCatalogPage(totalPages);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to load the capability catalog.");
        setCatalogRows([]);
        setSubsByCapabilityId(new Map());
        setCatalogTotalCount(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    catalogFilterableKeys,
    catalogPage,
    columnFilters,
    debouncedSearch,
    frameworkFilter,
    sourceTypeFilter,
  ]);

  const catalogTotalPages = Math.max(1, Math.ceil(catalogTotalCount / CAPABILITY_CATALOG_PAGE_SIZE));

  const handleCatalogPageChange = useCallback(
    (nextPage: number) => setCatalogPage(Math.min(Math.max(1, nextPage), catalogTotalPages)),
    [catalogTotalPages]
  );

  const handleCatalogColumnFilterChange = useCallback(
    (key: MasterListColumnKey, value: string) => {
      setColumnFilters((prev) => ({ ...prev, [key]: value }));
      setCatalogPage(1);
    },
    []
  );

  const clearFilters = useCallback(() => {
    setColumnFilters(emptyMasterListFilters(CAPABILITY_VIEW));
    setFrameworkFilter("");
    setSourceTypeFilter("");
    setCatalogPage(1);
  }, []);

  const columnFilterValue = (key: MasterListColumnKey) => {
    const selected = columnFilters[key];
    return !selected || selected === MASTER_LIST_FILTER_ANY ? "" : selected;
  };

  const setColumnFilterValue = (key: MasterListColumnKey, value: string) => {
    handleCatalogColumnFilterChange(key, value || MASTER_LIST_FILTER_ANY);
  };

  const frameworkOptions = frameworks.map((framework) => ({
    value: String(framework.id),
    label: framework.sourceFrameworkName,
  }));

  const sourceTypeOptions = sourceTypes
    .filter((type) => type.isActive !== false)
    .map((type) => ({ value: String(type.id), label: type.name }));

  const optionList = (key: MasterListColumnKey) =>
    (catalogFilterOptions[key] || [])
      .filter((option) => option && option !== "—")
      .map((option) => ({ value: option, label: option }));

  const filtersActive =
    Boolean(frameworkFilter) ||
    Boolean(sourceTypeFilter) ||
    ["capabilityIsActive", "frameworkReferenceCompany", "businessTypeName"].some((key) =>
      columnFilterValue(key as MasterListColumnKey)
    );

  const frameworkSummaryCount = (catalogFilterOptions.sourceFrameworkName || []).filter(
    (name) => name && name !== "—"
  ).length;

  // ── Selection ────────────────────────────────────────────────────────────
  const selectedCapabilityRows = useMemo(() => [...selectedCaps.values()], [selectedCaps]);
  const selectedSubRows = useMemo(() => [...selectedSubs.values()], [selectedSubs]);
  const selectedCapabilityKeys = useMemo(() => new Set(selectedCaps.keys()), [selectedCaps]);
  const selectedSubKeys = useMemo(() => new Set(selectedSubs.keys()), [selectedSubs]);

  const selectedSubsByCapabilityId = useMemo(
    () => groupRowsByCapabilityId(selectedSubRows),
    [selectedSubRows]
  );

  const getCapabilityStatus = useCallback(
    (row: MasterListRow) =>
      getCapabilityPickerStatus(
        row,
        selectedSubsByCapabilityId.get(row.capabilityId) || EMPTY_MASTER_LIST_ROWS
      ),
    [selectedSubsByCapabilityId]
  );

  const setCapabilities = useCallback((rows: MasterListRow[], selected: boolean) => {
    if (!rows.length) return;
    setSelectedCaps((prev) => {
      const next = new Map(prev);
      for (const row of rows) {
        if (selected) next.set(row.rowKey, row);
        else next.delete(row.rowKey);
      }
      return next;
    });
    if (!selected) {
      // Dropping a capability also drops its sub-capabilities.
      const ids = new Set(rows.map((row) => row.capabilityId));
      setSelectedSubs((prev) => {
        const next = new Map(prev);
        for (const [key, sub] of prev) {
          if (ids.has(sub.capabilityId)) next.delete(key);
        }
        return next.size === prev.size ? prev : next;
      });
    }
  }, []);

  const setSubs = useCallback(
    (subs: MasterListRow[], selected: boolean) => {
      if (!subs.length) return;
      setSelectedSubs((prev) => {
        const next = new Map(prev);
        for (const sub of subs) {
          if (selected) next.set(sub.rowKey, sub);
          else next.delete(sub.rowKey);
        }
        return next;
      });
      if (selected) {
        // Picking a sub-capability implies its parent capability.
        const parentIds = new Set(subs.map((sub) => sub.capabilityId));
        const parents = catalogRows.filter((row) => parentIds.has(row.capabilityId));
        setSelectedCaps((prev) => {
          const missing = parents.filter((row) => !prev.has(row.rowKey));
          if (!missing.length) return prev;
          const next = new Map(prev);
          for (const row of missing) next.set(row.rowKey, row);
          return next;
        });
      }
    },
    [catalogRows]
  );

  const selection = useMemo<CapabilityCatalogSelection>(
    () => ({
      selectedCapabilityKeys,
      selectedSubKeys,
      onToggleCapability: (row) => setCapabilities([row], !selectedCaps.has(row.rowKey)),
      onToggleSub: (sub) => setSubs([sub], !selectedSubs.has(sub.rowKey)),
      onSetCapabilities: setCapabilities,
      onSetSubs: setSubs,
      getCapabilityStatus,
    }),
    [
      getCapabilityStatus,
      selectedCapabilityKeys,
      selectedCaps,
      selectedSubKeys,
      selectedSubs,
      setCapabilities,
      setSubs,
    ]
  );

  const capsMissingSubs = useMemo(
    () => getCapabilitiesMissingSubSelection(selectedCapabilityRows, selectedSubRows),
    [selectedCapabilityRows, selectedSubRows]
  );

  const impliedQuestionCount = useMemo(
    () =>
      selectedCapabilityRows
        .filter((row) => row.subCapabilityCount === 0)
        .reduce((sum, row) => sum + (row.questionCount || 0), 0) +
      selectedSubRows.reduce((sum, row) => sum + (row.questionCount || 0), 0),
    [selectedCapabilityRows, selectedSubRows]
  );

  // Questions are expanded server-side from the selected capabilities / sub-capabilities.
  const selectedCatalogIds = useMemo(
    () => collectCatalogIdsFromMasterListRows([...selectedCapabilityRows, ...selectedSubRows]),
    [selectedCapabilityRows, selectedSubRows]
  );

  const incompleteCapabilityCount = capsMissingSubs.length;
  const canCreate = selectedCapabilityRows.length > 0 && capsMissingSubs.length === 0;
  const hasBlockingIssues = capsMissingSubs.length > 0;
  const shownMissingSubs = capsMissingSubs.slice(0, 8);
  const extraMissingSubs = capsMissingSubs.length - shownMissingSubs.length;

  useEffect(() => {
    if (!hasBlockingIssues) setShowIssueDetails(false);
  }, [hasBlockingIssues]);

  /** Brings a selected capability into view; searches for it when it is on another page. */
  const focusCapability = useCallback(
    (cap: MasterListRow) => {
      if (!catalogRows.some((row) => row.rowKey === cap.rowKey)) {
        setColumnFilters(emptyMasterListFilters(CAPABILITY_VIEW));
        setFrameworkFilter("");
        setSourceTypeFilter("");
        setSearchTerm(cap.capabilityName);
        setDebouncedSearch(cap.capabilityName);
        setCatalogPage(1);
      }
      setRevealCapabilityKey(cap.rowKey);
    },
    [catalogRows]
  );

  const clearReveal = useCallback(() => setRevealCapabilityKey(null), []);

  const openQuestionPreview = useCallback(
    async (owner: MasterListRow) => {
      setQuestionPreviewLoading(true);
      try {
        const detail = await getCapabilityDetail(owner.capabilityId);
        const questionRows = buildMasterListRows(
          "question",
          [detail],
          frameworkById,
          {},
          sourceTypeById
        );
        setQuestionPreview(buildQuestionPreview(owner, questionRows));
      } catch (err) {
        dashboardToast.error(
          err instanceof Error ? err.message : "Failed to load questions for preview."
        );
      } finally {
        setQuestionPreviewLoading(false);
      }
    },
    [frameworkById, sourceTypeById]
  );

  const renderPreviewButton = useCallback(
    (row: MasterListRow) => {
      const rowLabel = getMasterListRowLabel(row);
      return (
        <UiButton
          variant="outline"
          size="sm"
          className="btn-list-edit"
          onClick={() => void openQuestionPreview(row)}
          title="Preview questions"
          aria-label={`Preview questions for ${rowLabel}`}
        >
          <Eye />
        </UiButton>
      );
    },
    [openQuestionPreview]
  );

  const renderCapabilityActions = useCallback(
    (row: MasterListRow) => (row.subCapabilityCount === 0 ? renderPreviewButton(row) : null),
    [renderPreviewButton]
  );

  const openCreateModal = useCallback(() => {
    if (!canCreate) {
      if (capsMissingSubs.length > 0) {
        dashboardToast.error(formatMissingSubSelectionMessage(capsMissingSubs));
        return;
      }
      dashboardToast.error("Select at least one capability first.");
      return;
    }
    setCreateTitle("");
    setCreateDescription("");
    setShowCreateModal(true);
  }, [canCreate, capsMissingSubs]);

  const closeCreateModal = useCallback(() => {
    if (!creating) setShowCreateModal(false);
  }, [creating]);

  const submitCreate = useCallback(
    async (event) => {
      event.preventDefault();
      const title = createTitle.trim();
      if (!title) {
        dashboardToast.error("Title is required.");
        return;
      }
      if (!canCreate) {
        dashboardToast.error(
          formatMissingSubSelectionMessage(capsMissingSubs) || "Complete your selection first."
        );
        return;
      }

      setCreating(true);
      try {
        await addOrUpdateTraxccelFramework({
          id: 0,
          title,
          description: createDescription.trim(),
          isActive: true,
          ...selectedCatalogIds,
        });
        dashboardToast.success("Assessment framework created");
        setShowCreateModal(false);
        setSelectedCaps(emptyRowMap());
        setSelectedSubs(emptyRowMap());
        navigate("/sa/TraxccelFrameworks");
      } catch (err) {
        dashboardToast.error(err.message || "Failed to create assessment framework");
      } finally {
        setCreating(false);
      }
    },
    [canCreate, capsMissingSubs, createDescription, createTitle, navigate, selectedCatalogIds]
  );

  return (
    <DashboardShell
      isMenuCollapsed={isMenuCollapsed}
      sidebar={<Menu onSelect={handleMenuSelection} selectedItem={selectedItem}
          isCollapsed={isMenuCollapsed}
          onToggleCollapse={toggleMenuCollapse}
        />}
    >
      <SaListPageShell topBarTitle={FRAMEWORK_STUDIO_LABELS.assessmentFrameworks}>
        <div className="dash-frame-parent1 cap-catalog-page master-list-picker-page">
          <header className="cap-catalog-page__head">
            <div className="cap-catalog-page__intro">
              <h2 className="cap-catalog-page__title">
                {FRAMEWORK_STUDIO_LABELS.assessmentFrameworks}
              </h2>
              <p className="cap-catalog-page__desc">{MASTER_LIST_PAGE_COPY.description}</p>
              <div className="master-list-toolbar-stats" aria-live="polite">
                <span>{selectedCapabilityRows.length} selected</span>
                <span>{selectedSubRows.length} sub-capabilities</span>
                <span>{impliedQuestionCount} questions</span>
                {canCreate ? (
                  <span className="master-list-toolbar-stats__ready">
                    {FRAMEWORK_STUDIO_LABELS.readyToCreateAssessmentFramework}
                  </span>
                ) : incompleteCapabilityCount > 0 ? (
                  <button
                    type="button"
                    className={cx("master-list-toolbar-stats__warn", showIssueDetails && "is-open")}
                    aria-expanded={showIssueDetails}
                    onClick={() => setShowIssueDetails((open) => !open)}
                  >
                    {incompleteCapabilityCount} need attention
                    <ChevronDown aria-hidden />
                  </button>
                ) : null}
              </div>
            </div>
            <div className="cap-catalog-page__actions">
              <UiButton
                variant="primary"
                className="sa-create-btn"
                onClick={openCreateModal}
                disabled={!canCreate}
              >
                {FRAMEWORK_STUDIO_LABELS.createAssessmentFrameworkAction}
              </UiButton>
            </div>
          </header>

          {showIssueDetails && hasBlockingIssues ? (
            <div className="master-list-issues-banner">
              <p>Choose at least one sub-capability for:</p>
              <ul className="master-list-issue-list">
                {shownMissingSubs.map((cap) => (
                  <li key={cap.rowKey}>
                    <button
                      type="button"
                      className="master-list-issue-btn"
                      onClick={() => {
                        setShowIssueDetails(false);
                        focusCapability(cap);
                      }}
                    >
                      {cap.capabilityName}
                    </button>
                  </li>
                ))}
              </ul>
              {extraMissingSubs > 0 ? <p>And {extraMissingSubs} more.</p> : null}
            </div>
          ) : null}

          <div className="cap-catalog-page__table-shell master-list-page-content">
            <div className="cap-catalog-toolbar">
              <div className="dash-search-field cap-catalog-toolbar__search">
                <Search className="dash-search-field__icon" aria-hidden />
                <input
                  type="text"
                  placeholder="Search capabilities…"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  className="dash-search-field__input"
                />
              </div>
              <div className="cap-catalog-toolbar__filters">
                <CatalogFilterChip
                  label="Framework"
                  value={frameworkFilter}
                  options={frameworkOptions}
                  onChange={setFrameworkFilter}
                />
                <CatalogFilterChip
                  label={FRAMEWORK_STUDIO_LABELS.sourceType}
                  value={sourceTypeFilter}
                  options={sourceTypeOptions}
                  onChange={setSourceTypeFilter}
                />
                <CatalogFilterChip
                  label="Status"
                  value={columnFilterValue("capabilityIsActive")}
                  options={optionList("capabilityIsActive")}
                  onChange={(value) => setColumnFilterValue("capabilityIsActive", value)}
                />
                <CatalogFilterChip
                  label="Ref. company"
                  value={columnFilterValue("frameworkReferenceCompany")}
                  options={optionList("frameworkReferenceCompany")}
                  onChange={(value) => setColumnFilterValue("frameworkReferenceCompany", value)}
                />
                <CatalogFilterChip
                  label="User type"
                  value={columnFilterValue("businessTypeName")}
                  options={optionList("businessTypeName")}
                  onChange={(value) => setColumnFilterValue("businessTypeName", value)}
                />
                {filtersActive ? (
                  <button type="button" className="cap-catalog-toolbar__clear" onClick={clearFilters}>
                    Clear all
                  </button>
                ) : null}
              </div>
              <span className="cap-catalog-toolbar__meta">
                {catalogTotalCount === 1 ? "1 capability" : `${catalogTotalCount} capabilities`}
                {frameworkSummaryCount > 0
                  ? ` · ${frameworkSummaryCount === 1 ? "1 framework" : `${frameworkSummaryCount} frameworks`}`
                  : ""}
              </span>
            </div>
            {loading ? (
              <ContentLoader label="Loading capability catalog…" />
            ) : error ? (
              <Alert variant="error">{error}</Alert>
            ) : (
              <CapabilityCatalogTable
                rows={catalogRows}
                subsByCapabilityId={subsByCapabilityId}
                columnFilters={columnFilters}
                onColumnFilterChange={handleCatalogColumnFilterChange}
                onClearColumnFilters={clearFilters}
                filterOptionsByKey={catalogFilterOptions}
                page={catalogPage}
                totalPages={catalogTotalPages}
                totalCount={catalogTotalCount}
                onPageChange={handleCatalogPageChange}
                renderActions={renderCapabilityActions}
                renderSubActions={renderPreviewButton}
                selection={selection}
                revealCapabilityKey={revealCapabilityKey}
                onRevealed={clearReveal}
              />
            )}
          </div>

          <footer className="master-list-selection-bar">
            <div className="master-list-selection-bar__summary">
              <span className="master-list-selection-bar__text">
                {selectedCapabilityRows.length} caps · {selectedSubRows.length} subs ·{" "}
                {impliedQuestionCount} questions (auto-included)
              </span>
              {hasBlockingIssues ? (
                <p className="master-list-selection-bar__note">
                  Choose sub-capabilities for capabilities that have them.
                </p>
              ) : null}
            </div>
            <div className="master-list-selection-bar__actions">
              {capsMissingSubs[0] ? (
                <UiButton
                  variant="outline"
                  size="sm"
                  onClick={() => focusCapability(capsMissingSubs[0])}
                >
                  Open first
                </UiButton>
              ) : null}
              {selectedCapabilityRows.length > 0 ? (
                <UiButton
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setSelectedCaps(emptyRowMap());
                    setSelectedSubs(emptyRowMap());
                  }}
                >
                  Clear selection
                </UiButton>
              ) : null}
              <UiButton
                variant="primary"
                size="sm"
                onClick={openCreateModal}
                disabled={!canCreate}
              >
                <Layers size={14} aria-hidden />
                {FRAMEWORK_STUDIO_LABELS.createAssessmentFrameworkAction}
              </UiButton>
            </div>
          </footer>
        </div>
      </SaListPageShell>

      {questionPreviewLoading ? (
        <Modal show centered onHide={() => setQuestionPreviewLoading(false)}>
          <Modal.Body className="py-4 text-center text-muted">Loading questions…</Modal.Body>
        </Modal>
      ) : null}
      <QuestionsPreviewModal
        preview={questionPreview}
        onClose={() => setQuestionPreview(null)}
      />

      <Modal show={showCreateModal} onHide={closeCreateModal} centered>
        <Form onSubmit={submitCreate}>
          <Modal.Header closeButton>
            <Modal.Title>{FRAMEWORK_STUDIO_LABELS.createAssessmentFramework}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="master-list-create-summary mb-3">
              <p className="master-list-create-summary__lead">
                Your selection will be saved as an assessment framework. Question selection and
                editing happens when you create an assessment assignment.
              </p>
              <ul className="master-list-create-summary__stats">
                <li>
                  <span className="master-list-create-summary__value">
                    {selectedCatalogIds.frameworkIds.length}
                  </span>
                  <span className="master-list-create-summary__label">Frameworks</span>
                </li>
                <li>
                  <span className="master-list-create-summary__value">
                    {selectedCatalogIds.capabilityIds.length}
                  </span>
                  <span className="master-list-create-summary__label">Capabilities</span>
                </li>
                <li>
                  <span className="master-list-create-summary__value">
                    {selectedCatalogIds.subCapabilityIds.length}
                  </span>
                  <span className="master-list-create-summary__label">Sub-capabilities</span>
                </li>
                <li>
                  <span className="master-list-create-summary__value">{impliedQuestionCount}</span>
                  <span className="master-list-create-summary__label">Questions</span>
                </li>
              </ul>
            </div>
            <Form.Group className="mb-3" controlId="tfTitle">
              <Form.Label className="required">Title</Form.Label>
              <Form.Control
                value={createTitle}
                onChange={(event) => setCreateTitle(event.target.value)}
                placeholder="e.g. Enterprise AI Assessment Framework"
                required
                maxLength={250}
                autoFocus
              />
            </Form.Group>
            <Form.Group controlId="tfDescription">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={createDescription}
                onChange={(event) => setCreateDescription(event.target.value)}
                placeholder="Enter description"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={closeCreateModal} disabled={creating}>
              Cancel
            </Button>
            <Button type="submit" className="btn btn-dashboard-primary" disabled={creating}>
              {creating ? "Creating..." : "Create"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </DashboardShell>
  );
};

export default MasterList;
